import { Modal } from "flowbite-react";
import { Form, FormikProvider, useFormik } from "formik";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import * as Yup from "yup";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import { createFineAssasSubscription } from "../../../../../services/integration.api";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { numberToCurrency } from "../../../../../../../common/helpers/currency.helpers";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import { PaymentInfo } from "../../../../../../../types/payment.type";
import { InstallmentOption, useCalculateInstallmentOptions } from "../../../../../helpers/calculationUtils";
import { groupFinesByPlate } from "../../../../../helpers/methods/dataExtractDescription";
import { NegotiationFineForm } from "../form/FormNegotiationFine";
import { FormBillingFine } from "../form/FormBilingFine";

function isTodayOrFuture(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = new Date(dateString + 'T00:00:00');
  inputDate.setHours(0, 0, 0, 0);


  return inputDate >= today;
}

type Props = {
  onClosed: () => void;
  open: boolean
  trafficFines: PaymentInfo[]
}

export function ModalFine(props: Props) {
  const [selectedPlate, setSelectedPlate] = useState<string>("");
  const [selectedAction, setSelectedAction] = useState("");
  const [descriptionEdited] = useState(false);

  const handleCloseModal = () => { props.onClosed && props.onClosed() };
  const { notifyError, notifySuccess } = useToastContext();
  const { renterId } = useParams()
  const [, setSelectedInstallmentOption] = useState('0');
  const trafficFines = props.trafficFines;


  const validationSchema = Yup.object().shape({
    value: Yup.string().required('Valor da multa é obrigatório'),
    licensePlate: Yup.string().required('Placa do veículo é obrigatório'),
    installmentOption: Yup.string().when('actionSelection', {
      is: (value) => value === 'NegotiationFine',
      then: (schema) => schema.required('Período de cobrança é obrigatório'),
    }),
    description: Yup.string().required('Descrição da multa é obrigatório'),
    nextDueDate: Yup.string().required('Data da primeira parcela é obrigatório').test('nextDueDate',
      'A data não pode ser anterior a hoje.', value => isTodayOrFuture(value)),
    amoutTrafficNumbers: Yup.string().when('actionSelection', {
      is: (value) => value === 'NegotiationFine',
      then: (schema) => schema.required('Número do auto de infração é obrigatório'),
    }),
  });

  const formik = useFormik({
    initialValues: {
      value: 0,
      installmentValue: "",
      licensePlate: "",
      description: `PARCELAMENTO DE MULTAS DE TRANSITO DA 
      PLACA: ${selectedPlate} - REFERENTE AO AUTO DE INFRAÇÃO Nº: `,
      renterId: renterId || "",
      installmentOption: "",
      nextDueDate: "",
      paymentsIds: [],
      amoutTrafficNumbers: "",
      actionSelection: selectedAction
    },

    onSubmit: async (values) => {
      try {
        const { ok, message } = await createFineAssasSubscription(values)

        if (!ok && message) {
          notifyError(message);
        }

        if (ok) {
          notifySuccess("Multa criada com sucesso");
        }
      } catch (e) {
        console.error(e);
      }
    },
    validationSchema
  });

  const filteredTrafficFines = trafficFines.filter(
    (fine) => !fine.planId || !fine.planId.toLowerCase().includes("negotiationfine")
  );


  const groupedFines = groupFinesByPlate(filteredTrafficFines);

  const valueFineLicensePlate = groupedFines.reduce((acc, { totalFine }) => acc + totalFine, 0);

  const valueTotalFines =
    formik.values.value > 0 ? formik.values.value : valueFineLicensePlate;


  const calculateInstallmentOptions = useCalculateInstallmentOptions();
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions(valueTotalFines, 20);
  }, [valueTotalFines, calculateInstallmentOptions]);

  useEffect(() => {

    if (selectedPlate) {
      const group = groupedFines.find(g => g.licensePlate === selectedPlate);
      if (group) {
        const autoNumbers = group.autoNumbers.join('/ ');
        formik.setFieldValue('value', group.totalFine);
        formik.setFieldValue('paymentsIds', group.paymentIds);
        formik.setFieldValue('amoutTrafficNumbers', group.autoNumbers.join('/ '));


        if (!descriptionEdited) {
          const newDescription = `PARCELAMENTO DE MULTAS DE TRANSITO DA PLACA: ${selectedPlate}, REFERENTE AO AUTO DE INFRAÇÃO Nº: ${autoNumbers} NO VALOR DE ${numberToCurrency(formik.values.value)} EM ${formik.values.installmentOption}X`;
          formik.setFieldValue('description', newDescription);
        }
      }
    }
    const valueSelectedOption = installmentOptions.find(
      (option) => option.value === formik.values.installmentOption
    );
    const installmentAmount = valueSelectedOption?.valueOption || '';
    if (installmentAmount && formik.values.installmentValue !== installmentAmount) {
      formik.setFieldValue('installmentValue', installmentAmount);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlate, installmentOptions, formik.values.installmentOption, descriptionEdited]);

  const handleInstallmentOptionChange = useCallback((option) => {
    setSelectedInstallmentOption(option);
  }, []);

  const handleActionChange = (value: string) => {
    setSelectedAction(value);
  };

  const handleSelectedPlate = (plate: string) => {
    setSelectedPlate(plate);
  }



  return (
    <Modal
      show={props.open}
      onClose={handleCloseModal}
      className="dark:text-black">
      <Modal.Header className="text-black">
        <div className="flex items-baseline justify-center bg-gray-100">
          <h2 className="ml-2 text-black">Negociar Multa</h2>
        </div>
      </Modal.Header>
      <Modal.Body>
        <FormikProvider value={formik}>
          <Form>
            {/* Mova o ComboBoxFormik para dentro do Form */}
            <ComboBoxFormik
              name="actionSelection"
              label="Selecione a ação:"
              options={[
                { value: "-", label: "-" },
                { value: "BillingFine", label: "Lançar Multas" },
                { value: "NegotiationFine", label: "Negociar Multas" }
              ]}
              required={true}
              onChange={(value) => handleActionChange(value as string)}
              value={selectedAction}
            />

            {/* Mostra conteúdo apenas quando uma opção válida é selecionada */}
            {selectedAction === "NegotiationFine" && (
              <>
                <NegotiationFineForm
                  groupedFines={groupedFines}
                  installmentOptions={installmentOptions}
                  handleInstallmentOptionChange={handleInstallmentOptionChange}
                  handleSelectedPlate={handleSelectedPlate}
                />
                <div className="flex justify-end col-span-5 mt-10">
                  <ButtonWithLoader type="submit" isLoading={formik.isSubmitting}>
                    Enviar
                  </ButtonWithLoader>
                </div>
              </>
            )}

            {selectedAction === "BillingFine" && (
              <>
                <FormBillingFine />
                <div className="flex justify-end col-span-5 mt-10">
                  <ButtonWithLoader type="submit" isLoading={formik.isSubmitting}>
                    Enviar
                  </ButtonWithLoader>
                </div>
              </>
            )}
          </Form>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  );
}